// These types were provided by Frontegg via slack. Ideally, they expose these types via an npm package (currently is in a private repo).
// Until then, reach out to the Frontegg team to get additional needed types/interfaces.

// ========================================
// Pagination & Base Types
// ========================================

export interface FronteggPaginationLinks {
  next: string;
  prev: string;
  first: string;
  last: string;
}

export interface FronteggPaginationResult<T> {
  _metadata: {
    totalPages: number;
    totalItems: number;
  };
  items: T[];
}

export interface FronteggPaginationWrapper<T>
  extends FronteggPaginationResult<T> {
  _links?: FronteggPaginationLinks;
}

export enum PaginationOrderEnum {
  ASC = 'ASC',
  DESC = 'DESC',
}

// ========================================
// User Query Params
// ========================================

export enum SortByEnum {
  createdAt = 'createdAt',
  name = 'name',
  email = 'email',
  id = 'id',
  verified = 'verified',
  isLocked = 'isLocked',
  provider = 'provider',
  tenantId = 'tenantId',
}

export interface IGetUserQueryParams {
  _limit?: number;
  _offset?: number;
  ids?: string;
  _sortBy?: SortByEnum;
  _order?: PaginationOrderEnum;
}

export interface ISearchUserQueryParamsV2 extends IGetUserQueryParams {
  _filter?: string;
  metadata?: string;
  _includeSubTenants?: boolean;
}

export interface ISearchUserQueryParamsV3 extends IGetUserQueryParams {
  _email?: string;
  _tenantId?: string;
  _includeSubTenants?: boolean;
  _preset?: GetUsersFilterPreset;
  _maxInactiveSeconds?: number;
  _identifier?: string;
  _identifierType?: string;
  _namePrefix?: string;
}

export enum GetUsersFilterPreset {
  MFA_UNENROLLED = 'mfa-unenrolled',
  BREACHED_PASSWORDS = 'breached-passwords',
  INACTIVE = 'inactive',
}

// ========================================
// User Mutation Types
// ========================================
//

// Note than in frontegg docs, this would be an "activation email"
export interface ResendUserInvitationEmailPayload {
  email: string;
  emailMetadata: object;
}

// ========================================
// User Response Types
// ========================================

export enum UserManagedByEnum {
  FRONTEGG = 'frontegg',
  SCIM = 'scim',
}

export interface IRole {
  id: string;
  key: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

export interface ITeamUserPermission {
  id: string;
  key: string;
  name: string;
  description?: string;
  categoryId?: string;
}

export interface IGroupResponse {
  id: string;
  name: string;
  description?: string;
  color?: string;
  roles?: IRole[];
}

export interface IUserTenantData {
  tenantId: string;
  roles: IRole[];
}

export interface IBaseUserData {
  id: string;
  email: string;
  name: string;
  profilePictureUrl?: string | null;
}

export interface IBaseGetUserResponse extends IBaseUserData {
  sub: string;
  verified: boolean;
  mfaEnrolled: boolean;
  mfaBypass?: boolean;
  phoneNumber?: string;
  provider?: string;
  tenantId: string;
  tenantIds: string[];
  activatedForTenant?: boolean;
  isLocked?: boolean;
  tenants: IUserTenantData[];
  managedBy?: UserManagedByEnum;
  invisible?: true;
  superUser?: true;
  metadata: string;
  createdAt?: Date;
  lastLogin?: Date | string | null;
  subAccountAccessAllowed?: boolean;
  temporaryExpirationDate?: Date;
  isDisabled?: boolean;
}

export interface IGetUsersV2Response extends IBaseGetUserResponse {
  roles: IRole[];
  permissions: ITeamUserPermission[];
  groups?: Pick<IGroupResponse, 'id' | 'roles'>[];
}
